import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { GenerationJob } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

import { AnchoredPopover } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AnchoredPopover";

import { isGenerationJobReady } from "../utils/isGenerationJobReady";

const initials = (name: string | null): string =>
	(name ?? "")
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0]?.toUpperCase() ?? "")
		.join("");

interface AiQueuePillProps {
	/** Queue-ordered live jobs (`selectGenerationJobs`); hidden when empty. */
	jobs: GenerationJob[];
	/**
	 * Go to the picked lead — the host selects that lead's card and, for a ready job,
	 * reviews it (D6). Every row navigates, whatever its status.
	 */
	onSelectJob: (job: GenerationJob) => void;
}

/**
 * The AI generation queue pill (PREC-3635 P6 D7, mockup `renderAiQueue`):
 * "⟳ Generating N sequences · M ready" with a per-job dropdown. Purely
 * presentational so list pages can mount it later — everything it needs
 * arrives via props.
 *
 * Each row is the control: picking a lead takes you to its card, so the "Review"
 * affordance is a label rather than a nested button.
 */
export const AiQueuePill: FunctionComponent<AiQueuePillProps> = ({ jobs, onSelectJob }) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	if (jobs.length === 0) {
		return null;
	}

	const activeCount = jobs.filter(
		(job) => job.status === "QUEUED" || job.status === "RUNNING"
	).length;
	const readyCount = jobs.filter(isGenerationJobReady).length;

	const renderStatus = (job: GenerationJob) => {
		if (job.status === "RUNNING") {
			return (
				<span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-brand-light">
					<Icon className="h-3 w-3 animate-spin" icon={faSpinner} />
					{t("feed.aiQueue.statusGenerating")}
				</span>
			);
		}

		if (job.status === "QUEUED") {
			return (
				<span className="shrink-0 text-xs font-semibold text-text-tertiary">
					{t("feed.aiQueue.statusQueued")}
				</span>
			);
		}

		return (
			<span
				className="shrink-0 rounded-md bg-background-success px-3 py-1 text-xs font-bold text-text-success"
				data-testid={`feed-ai-queue-review-${job.listPersonId}`}
			>
				{t("feed.aiQueue.review")}
			</span>
		);
	};

	return (
		<>
			<button
				className={classNames(
					"flex h-8 cursor-pointer items-center gap-1.5 rounded-[10px] border px-2.5 text-xs font-semibold",
					activeCount > 0
						? "border-brand-light bg-light-blue text-brand-light"
						: "border-transparent bg-background-success text-text-success"
				)}
				data-testid="feed-ai-queue-pill"
				type="button"
				onClick={(event) =>
					setAnchorEl((current) => (current ? null : (event.currentTarget as HTMLElement)))
				}
			>
				{readyCount > 0 && (
					<span className="text-text-success">
						{t("feed.aiQueue.ready", { count: readyCount })}
					</span>
				)}
				{activeCount > 0 && readyCount > 0 && <span className="opacity-50">–</span>}
				{activeCount > 0 && (
					<>
						<Icon className="h-3 w-3 animate-spin" icon={faSpinner} />
						<span>{t("feed.aiQueue.generating", { count: activeCount })}</span>
					</>
				)}
				<Icon className="h-2.5 w-2.5 opacity-60" icon={faChevronDown} />
			</button>

			{anchorEl && (
				<AnchoredPopover anchorEl={anchorEl} width={300} onClose={() => setAnchorEl(null)}>
					<div
						className="overflow-hidden rounded-xl border border-border-light bg-white shadow-lg"
						data-testid="feed-ai-queue-menu"
					>
						<div className="border-b border-border-light px-3.5 py-2.5 text-[11px] font-bold uppercase tracking-wide text-text-tertiary">
							{t("feed.aiQueue.title")}
						</div>
						{jobs.map((job) => (
							<button
								key={job.listPersonId}
								className="flex w-full cursor-pointer items-center gap-2.5 border-b border-border-light px-3 py-2.5 text-left duration-150 last:border-b-0 hover:bg-gray-100"
								data-testid={`feed-ai-queue-row-${job.listPersonId}`}
								type="button"
								onClick={() => {
									setAnchorEl(null);
									onSelectJob(job);
								}}
							>
								<span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg bg-light-blue text-[10px] font-bold text-brand-light">
									{job.personAvatarUrl ? (
										<img alt="" className="h-full w-full object-cover" src={job.personAvatarUrl} />
									) : (
										initials(job.personName)
									)}
								</span>
								<span className="min-w-0 flex-1">
									<span className="block truncate text-[13px] font-semibold text-text">
										{job.personName}
									</span>
									<span className="block truncate text-[11.5px] text-text-tertiary">
										{job.companyName}
									</span>
								</span>
								{renderStatus(job)}
							</button>
						))}
					</div>
				</AnchoredPopover>
			)}
		</>
	);
};
